import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response, Pagination, SelectItemProps } from '@ui/src';
import { BlacklistValidators, Blacklist, BlacklistData } from '../../../types';
import { blacklistApi } from '../services';
import ROUTES from '../../../../routes';

export interface BlackListState {
  validators?: BlacklistValidators;
  values?: Blacklist[];
  searchResult?: BlacklistData[];
  searchResultPagination?: Pagination;
  searchText: string;
  sort?: {
    sortProp: string;
    sortDir: string;
  };
  hideInactive?: boolean;
  selectedLists: string[];
  pagination?: Pagination;
  selectedBlacklistsIds?: string[];
  blacklistsLists?: SelectItemProps[];
  skipValue?: number;
}

const initialState: BlackListState = {
  values: undefined,
  hideInactive: true,
  searchText: '',
  selectedLists: [],
  skipValue: undefined,
};

export const blacklistSlice = createSlice({
  name: 'blacklist',
  initialState,
  reducers: {
    onSortBlacklists: (
      state,
      action: PayloadAction<{ name: string; direction: 'asc' | 'desc' }>,
    ) => {
      state.sort = {
        sortDir: action.payload.direction,
        sortProp: action.payload.name,
      };
    },
    onSearch: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    onSelectBlacklistsIds: (state, action: PayloadAction<string[]>) => {
      state.selectedBlacklistsIds = action.payload;
    },
    setSkipValue: (state, action: PayloadAction<number>) => {
      state.skipValue = action.payload;
    },
    hideInactive: (state, action: PayloadAction<boolean>) => {
      state.hideInactive = action.payload;
    },
    selectList: (state, action: PayloadAction<string[]>) => {
      state.selectedLists = action.payload;
    },
    clearBlacklistsState: (state) => {
      state.values = undefined;
      state.searchResult = undefined;
      state.searchText = '';
      state.selectedLists = [];
      state.selectedBlacklistsIds = [];
      state.skipValue = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      blacklistApi.endpoints.getBlacklistValidators.matchFulfilled,
      (state, action: PayloadAction<{ data: BlacklistValidators }>) => {
        state.validators = action.payload.data;
      },
    );
    builder.addMatcher(
      blacklistApi.endpoints.getBlacklists.matchFulfilled,
      (state, action: PayloadAction<Response<Blacklist>>) => {
        state.values = action.payload.data.edges.map((item) => ({
          ...item.node,
          url: generatePath(ROUTES.BLACKLIST_ITEM, {
            itemId: item.node.id,
          }),
        }));

        state.pagination = action.payload.data.pageInfo;

        state.searchResult = undefined;
      },
    );
    builder.addMatcher(
      blacklistApi.endpoints.getBlacklistsItems.matchFulfilled,
      (state, action: PayloadAction<Response<Blacklist>>) => {
        state.blacklistsLists = action.payload.data.edges.map((item) => ({
          name: item.node.name,
          value: item.node.id,
        }));
      },
    );
    builder.addMatcher(
      blacklistApi.endpoints.getBlacklistsData.matchFulfilled,
      (state, action: PayloadAction<Response<BlacklistData>>) => {
        state.searchResult = action?.payload?.data?.edges?.map((item) => ({
          ...item.node,
          source: item?.node?.source,
          createdAt: item?.node?.source?.createdAt,
          lastUpdated: item?.node?.source?.lastUpdated,
          name:
            item?.node?.source?.firstName || item?.node?.source?.lastName
              ? `${item?.node?.source?.firstName || ''} ${
                  item?.node.source?.lastName || ''
                }`
              : item?.node.source?.name || '-',
          blacklistId: item?.node?.source?.blacklistId,
        }));
        state.searchResultPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      blacklistApi.endpoints.toggleBlacklistStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: Blacklist }>) => {
        state.values = state.values?.map((item) => {
          let result = { ...item };

          if (item.id === action.payload.data.id) {
            result = {
              ...action.payload.data,
              url: generatePath(ROUTES.BLACKLIST_ITEM, {
                itemId: action.payload.data.id,
              }),
            };
          }

          return result;
        });
      },
    );
  },
});

export const {
  onSortBlacklists,
  onSearch,
  hideInactive,
  selectList,
  clearBlacklistsState,
  onSelectBlacklistsIds,
  setSkipValue,
} = blacklistSlice.actions;

export default blacklistSlice.reducer;
